/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/**
 * AnticipateEmergingThemesV2 — the Emerging Themes tab, aligned to the SAME V2 theme model as the
 * Dashboard tab (gated by tenant.modules.horizonV2), but rendered as the RICH per-theme view the tab
 * is valued for: a filterable list of the fixed-L2 themes → click a theme for its "What's happening" /
 * "Why it matters" assessment, a trend-over-time chart (article volume by priority band), and the
 * news stream (incidents → articles). One theme vocabulary across both tabs, full detail preserved.
 * Data: GET /anticipateHorizonScan/v2/model (list) + GET /v2/theme-detail?l2=&days= (detail + trend).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import CircularProgress from "@mui/material/CircularProgress";
import MuiTooltip from "@mui/material/Tooltip";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const API = protectedResources.apiTodoList.endpoint;
// Palette from the app's Material-Dashboard theme (assets/theme/base/colors.js) — matches the product,
// not a bespoke set. accent = info.main (brand), ink = dark.main, muted = text.focus, lines = grey.300/400.
const C = {
  surface: "#ffffff",
  ink: "#344767",
  muted: "#7b809a",
  line: "#dee2e6",
  lineStrong: "#ced4da",
  accent: "#52009b",
  accentSoft: "#ede7f6",
  hot: "#fb8c00",
};
// Priority-band colours (P1 most urgent → P5 least); Unclassified is grey.
const PRIORITY_COLOR = {
  P1: "#c62828",
  P2: "#ef6c00",
  P3: "#f9a825",
  P4: "#2e7d32",
  P5: "#66bb6a",
  Unclassified: "#bdbdbd",
};
const levelColor = (label) => PRIORITY_COLOR[label] || "#bdbdbd";

const nf = (n) => Number(n || 0).toLocaleString();
const clean = (s) =>
  String(s || "")
    .replace(/\s+/g, " ")
    .trim();
const pretty = (t) =>
  String(t || "")
    .replace(/-/g, " ")
    .replace(/\b([a-z])/g, (m, c) => c.toUpperCase())
    .replace(/\b(Ai|It|Saf|Atc|Eu|Us|Uk|Mro|Ndc|Ceo|Esg)\b/gi, (s) => s.toUpperCase());

// Group a theme-detail's incidents under their sub-themes (same grouping the Dashboard card shows).
// Returns [{ name, meta, incidents }]. When drilled into a single sub-theme, or a theme has ≤1
// sub-theme, returns one unlabelled group so the caller renders a flat list.
function groupIncidentsBySubTheme(detail) {
  const incidents = detail?.incidents || [];
  const subThemes = detail?.subThemes || [];
  if (detail?.scoped || subThemes.length <= 1) return [{ name: "", meta: null, incidents }];
  const order = subThemes.map((s) => s.name);
  const byName = new Map(order.map((n) => [n, []]));
  const other = [];
  incidents.forEach((inc) => {
    if (inc.subTheme && byName.has(inc.subTheme)) byName.get(inc.subTheme).push(inc);
    else other.push(inc);
  });
  const groups = order
    .map((n) => ({
      name: n,
      meta: subThemes.find((s) => s.name === n) || null,
      incidents: byName.get(n),
    }))
    .filter((g) => g.incidents.length);
  if (other.length) groups.push({ name: "More developments", meta: null, incidents: other });
  return groups;
}
const fmtDay = (d) => {
  const parts = String(d || "").split("-");
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}`;
};

// ID188 — the priority pop-up: heading ("Priority N Theme") + the final consequence (level + type)
// and likelihood band. Matches the dashboard pop-up so both tabs behave the same.
function priorityPopup(p) {
  const cons = [p.consequenceLevelLabel, p.consequenceTypeLabel].filter(Boolean).join(" ");
  return (
    <span style={{ display: "block", fontSize: "0.72rem", lineHeight: 1.55, minWidth: 150 }}>
      <span
        style={{
          display: "block",
          fontWeight: 800,
          fontSize: "0.78rem",
          paddingBottom: 4,
          marginBottom: 5,
          borderBottom: "1px solid rgba(255,255,255,0.25)",
        }}
      >
        Priority {p.priorityBand} Theme
      </span>
      <span style={{ display: "block" }}>
        <b>Consequence:</b> {cons || "—"}
      </span>
      <span style={{ display: "block" }}>
        <b>Likelihood:</b> {p.plausibilityLabel || "—"}
      </span>
    </span>
  );
}

function PriorityBadge({ p }) {
  if (!p || !p.priorityBand)
    return (
      <MDTypography variant="caption" component="span" sx={{ color: C.muted, display: "inline" }}>
        —
      </MDTypography>
    );
  return (
    <MuiTooltip arrow placement="top" title={priorityPopup(p)}>
      <span
        style={{
          fontSize: "0.6875rem",
          fontWeight: 750,
          color: "#fff",
          background: p.matrixRatingColor || "#5B6B78",
          borderRadius: 6,
          padding: "1px 8px",
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
          cursor: "help",
        }}
      >
        P{p.priorityBand}
      </span>
    </MuiTooltip>
  );
}

const TH = {
  textAlign: "left",
  fontSize: "0.66rem",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: C.muted,
  fontWeight: 700,
  padding: "8px 12px",
  borderBottom: `1px solid ${C.lineStrong}`,
  whiteSpace: "nowrap",
};
const TD = {
  padding: "10px 12px",
  borderBottom: `1px solid ${C.line}`,
  fontSize: "0.82rem",
  color: C.ink,
  verticalAlign: "top",
};

/* ---------------- Detail view (rich): assessment + trend + news stream ---------------- */
function ThemeDetail({ detail, loading, trendDays, onTrendDays, onBack, onOpenAttachment }) {
  const chartData = useMemo(() => {
    const days = detail?.trend?.days || [];
    return days.map((d) => ({ day: d.day, ...d.byLevel }));
  }, [detail]);
  const levels = detail?.trend?.levels || [];
  const p = detail?.prioritisation;
  // Sub-theme groups collapse by default (same behaviour as the Dashboard drill-down pop-up).
  const [expandedSubs, setExpandedSubs] = useState({});
  const toggleSub = useCallback(
    (name) => setExpandedSubs((prev) => ({ ...prev, [name]: !prev[name] })),
    []
  );

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <MDButton size="small" variant="outlined" color="secondary" onClick={onBack}>
          ← All Emerging Themes
        </MDButton>
        <MDTypography variant="h5" sx={{ letterSpacing: "-0.01em" }}>
          {clean(detail?.theme)}
        </MDTypography>
        {detail?.l1 && (
          <Chip
            label={`${clean(detail.l1)} › ${clean(detail.theme)}`}
            size="small"
            sx={{ bgcolor: C.accentSoft, color: C.accent, fontWeight: 600 }}
          />
        )}
      </Box>

      {loading && (
        <Box sx={{ py: 6, textAlign: "center", color: C.muted }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!loading && detail && (
        <>
          {detail.noNews && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "10px",
                border: `1px dashed ${C.lineStrong}`,
                background: "#F6F8FA",
                color: C.muted,
                fontSize: "0.85rem",
              }}
            >
              <b style={{ color: C.ink }}>On the radar — no news yet.</b> This theme was flagged by
              a top-down (CRO / horizon-scanning) lens but has no relevant news coverage in the
              current window. It will populate with a trend and news stream once matching alerts
              arrive.
            </Box>
          )}
          {/* Assessment: risk level + What's happening + Why it matters */}
          <Card variant="outlined" sx={{ mb: 2.5, borderColor: C.line }}>
            <CardContent sx={{ pb: "14px !important" }}>
              <Box
                sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}
              >
                <PriorityBadge p={p} />
                {p?.consequenceLevelLabel && (
                  <MDTypography
                    variant="caption"
                    component="span"
                    sx={{ color: C.muted, display: "inline" }}
                  >
                    {p.consequenceLevelLabel} · {p.plausibilityLabel}
                  </MDTypography>
                )}
              </Box>
              {detail.summary && (
                <Box sx={{ mb: detail.whyItMatters ? 1.5 : 0 }}>
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ mb: 0.5, color: C.ink }}
                  >
                    What&apos;s happening
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: C.muted, lineHeight: 1.7 }}>
                    {detail.summary}
                  </MDTypography>
                </Box>
              )}
              {detail.whyItMatters && (
                <Box>
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ mb: 0.5, color: C.accent }}
                  >
                    Why it matters
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: C.muted, lineHeight: 1.7 }}>
                    {detail.whyItMatters}
                  </MDTypography>
                </Box>
              )}
              {!detail.summary && !detail.whyItMatters && (
                <MDTypography variant="body2" sx={{ color: C.muted, fontStyle: "italic" }}>
                  Assessment will appear after the next model rebuild.
                </MDTypography>
              )}
            </CardContent>
          </Card>

          {/* Trend over time: article volume by consequence level */}
          <Card variant="outlined" sx={{ mb: 2.5, borderColor: C.line }}>
            <CardContent sx={{ pb: "14px !important" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <MDTypography variant="subtitle2" fontWeight="bold">
                  Trend over time — volume by priority
                </MDTypography>
                <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
                  {[30, 90, 365].map((d) => (
                    <Box
                      key={d}
                      onClick={() => onTrendDays(d)}
                      sx={{
                        px: "8px",
                        py: "1px",
                        borderRadius: "4px",
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        border: `1px solid ${C.accent}`,
                        bgcolor: trendDays === d ? C.accent : "transparent",
                        color: trendDays === d ? "#fff" : C.accent,
                      }}
                    >
                      {d}d
                    </Box>
                  ))}
                </Box>
              </Box>
              {chartData.length === 0 ? (
                <MDTypography variant="body2" sx={{ color: C.muted }}>
                  No trend data in this window.
                </MDTypography>
              ) : (
                <Box sx={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 28 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" />
                      <XAxis
                        dataKey="day"
                        tickFormatter={fmtDay}
                        angle={-45}
                        textAnchor="end"
                        tick={{ fontSize: 11 }}
                        interval={Math.max(0, Math.floor(chartData.length / 12) - 1)}
                      />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={28} />
                      <Tooltip labelFormatter={(l) => `Day: ${l}`} />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                      {levels.map((lvl, i) => (
                        <Bar
                          key={lvl}
                          dataKey={lvl}
                          stackId="lvl"
                          name={lvl}
                          fill={levelColor(lvl, i, levels.length)}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* News stream: incidents → articles */}
          <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: C.ink, mb: 1 }}>
            News Stream
          </MDTypography>
          {(detail.incidents || []).length === 0 && (
            <MDTypography variant="body2" sx={{ color: C.muted }}>
              No news stream for this theme.
            </MDTypography>
          )}
          {groupIncidentsBySubTheme(detail).map((grp, gi) => {
            const collapsible = !!grp.name;
            const open = !collapsible || !!expandedSubs[grp.name];
            return (
              <Box key={gi} sx={{ mb: grp.name ? 2 : 0 }}>
                {grp.name && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleSub(grp.name)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleSub(grp.name);
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      borderLeft: `3px solid ${C.accent}`,
                      paddingLeft: 8,
                      paddingTop: 4,
                      paddingBottom: 4,
                      marginBottom: open ? 10 : 0,
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ color: C.accent, fontWeight: 800, width: 10, flex: "none" }}>
                      {open ? "▾" : "▸"}
                    </span>
                    <PriorityBadge p={grp.meta?.prioritisation} />
                    <MDTypography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: C.ink, flex: 1, minWidth: 0 }}
                    >
                      {clean(grp.name)}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      component="span"
                      sx={{
                        color: C.muted,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {nf(
                        grp.meta?.alertCount ??
                          grp.incidents.reduce((a, x) => a + (x.count || 0), 0)
                      )}{" "}
                      alerts
                    </MDTypography>
                  </div>
                )}
                {open &&
                  grp.incidents.map((inc, i) => (
                    <Box key={i} sx={{ mb: 2, pl: grp.name ? 1.5 : 0 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          gap: 10,
                          marginBottom: 5,
                        }}
                      >
                        <MDTypography variant="body2" sx={{ color: C.ink, fontWeight: 700 }}>
                          {pretty(inc.eventTag)}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          component="span"
                          sx={{ color: C.muted, whiteSpace: "nowrap", display: "inline" }}
                        >
                          {nf(inc.count)} {inc.count === 1 ? "article" : "articles"}
                        </MDTypography>
                      </div>
                      {(inc.articles || []).map((a, j) => (
                        <Box
                          key={j}
                          sx={{ pl: 1.5, py: 0.6, borderLeft: `2px solid ${C.line}`, mb: 0.6 }}
                        >
                          <a
                            href={a.link || "#"}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: "0.84rem", color: C.accent, fontWeight: 600 }}
                          >
                            {a.title || "(untitled)"}
                          </a>
                          <MDTypography variant="caption" sx={{ color: C.muted, marginTop: "2px" }}>
                            {a.source || ""}
                            {a.ingestedAt
                              ? ` · ${new Date(a.ingestedAt).toLocaleDateString()}`
                              : ""}
                            {a.relevanceScore != null ? ` · relevance ${a.relevanceScore}` : ""}
                          </MDTypography>
                          {a.attachmentBlob && (
                            <button
                              type="button"
                              onClick={() => onOpenAttachment(a.attachmentBlob)}
                              style={{
                                border: "none",
                                background: "none",
                                padding: 0,
                                marginTop: 3,
                                cursor: "pointer",
                                color: C.accent,
                                fontSize: "0.73rem",
                                fontWeight: 600,
                                textDecoration: "underline",
                              }}
                            >
                              📎 View source document
                              {a.attachmentName ? ` (${a.attachmentName})` : ""}
                            </button>
                          )}
                          {a.whyItMatters && (
                            <MDTypography variant="caption" sx={{ color: C.ink, marginTop: "3px" }}>
                              {a.whyItMatters}
                            </MDTypography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ))}
              </Box>
            );
          })}
        </>
      )}
    </>
  );
}

/* ---------------- List view ---------------- */
function AnticipateEmergingThemesV2() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterL1, setFilterL1] = useState("");
  const [maxBand, setMaxBand] = useState("all"); // priority filter: exact band 1|2|3, or "all"

  const [selected, setSelected] = useState(null); // selected theme name | null
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [trendDays, setTrendDays] = useState(90);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await executeRef.current("GET", `${API}/anticipateHorizonScan/v2/model`);
      if (res?.success) setDoc(res.data || null);
    } catch (e) {
      /* handled by hook */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadDetail = useCallback((themeName, days) => {
    setDetailLoading(true);
    executeRef
      .current(
        "GET",
        `${API}/anticipateHorizonScan/v2/theme-detail?l2=${encodeURIComponent(
          themeName
        )}&days=${days}`
      )
      .then((res) => {
        if (res?.success) setDetail(res.data || { theme: themeName, incidents: [] });
      })
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  }, []);

  const openTheme = useCallback(
    (themeName) => {
      setSelected(themeName);
      setDetail(null);
      loadDetail(themeName, trendDays);
    },
    [loadDetail, trendDays]
  );

  const changeTrendDays = useCallback(
    (d) => {
      setTrendDays(d);
      if (selected) loadDetail(selected, d);
    },
    [selected, loadDetail]
  );

  // Open an uploaded source document via a fresh short-lived SAS URL.
  const openAttachment = useCallback((blobName) => {
    if (!blobName) return;
    executeRef
      .current(
        "GET",
        `${API}/anticipateHorizonScan/v2/attachment?blobName=${encodeURIComponent(blobName)}`
      )
      .then((res) => {
        if (res?.success && res.data?.url) window.open(res.data.url, "_blank", "noopener");
      })
      .catch(() => {});
  }, []);

  const model = doc?.model || null;
  const allThemes = useMemo(() => {
    if (!model) return [];
    return (model.groups || []).flatMap((g) => (g.themes || []).map((t) => ({ ...t, l1: g.l1 })));
  }, [model]);

  const l1Options = useMemo(() => {
    const seen = [];
    (model?.groups || []).forEach((g) => {
      if (g.l1 && !seen.includes(g.l1)) seen.push(g.l1);
    });
    return seen;
  }, [model]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (
      allThemes
        // Exact-band selection: show ONLY the chosen priority (P1 / P2 / P3), or everything for "All".
        .filter((t) => maxBand === "all" || (t.prioritisation?.priorityBand || 99) === maxBand)
        .filter((t) => !filterL1 || t.l1 === filterL1)
        .filter((t) => {
          if (!q) return true;
          const hay = [t.theme, t.l1, ...(t.subThemes || []).map((s) => s.name)]
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        })
        .sort(
          (a, b) =>
            (a.prioritisation?.priorityBand || 99) - (b.prioritisation?.priorityBand || 99) ||
            (b.recentAlerts || 0) - (a.recentAlerts || 0)
        )
    );
  }, [allThemes, search, filterL1, maxBand]);

  // Detail view takes over the tab.
  if (selected) {
    return (
      <MDBox sx={{ color: C.ink }}>
        <ThemeDetail
          detail={detail}
          loading={detailLoading}
          trendDays={trendDays}
          onTrendDays={changeTrendDays}
          onBack={() => {
            setSelected(null);
            setDetail(null);
          }}
          onOpenAttachment={openAttachment}
        />
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ color: C.ink }}>
      <Box sx={{ mb: 2 }}>
        <MDTypography
          variant="caption"
          sx={{
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: C.accent,
            fontWeight: 700,
          }}
        >
          Anticipate · Horizon Scan
        </MDTypography>
        <MDTypography variant="h4" sx={{ letterSpacing: "-0.02em", mt: 0.5 }}>
          Emerging Themes
        </MDTypography>
        <MDTypography variant="button" sx={{ color: C.muted }}>
          The same risk themes as the Dashboard. Click a theme for what&apos;s happening, why it
          matters, the trend over time, and the news stream.
        </MDTypography>
      </Box>

      {!loading && model && (
        <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, flex: "1 1 200px" }}>
            <MDTypography
              variant="caption"
              component="span"
              sx={{ color: C.muted, fontWeight: 700, display: "inline" }}
            >
              Search
            </MDTypography>
            <TextField
              size="small"
              placeholder="Search themes & sub-themes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
            <MDTypography
              variant="caption"
              component="span"
              sx={{ color: C.muted, fontWeight: 700, display: "inline" }}
            >
              Category
            </MDTypography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select value={filterL1} displayEmpty onChange={(e) => setFilterL1(e.target.value)}>
                <MenuItem value="">All categories</MenuItem>
                {l1Options.map((l1) => (
                  <MenuItem key={l1} value={l1}>
                    {clean(l1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
            <MDTypography
              variant="caption"
              component="span"
              sx={{ color: C.muted, fontWeight: 700, display: "inline" }}
            >
              Priority
            </MDTypography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {[
                { v: 1, l: "P1" },
                { v: 2, l: "P2" },
                { v: 3, l: "P3" },
                { v: "all", l: "All" },
              ].map((b) => (
                <MDButton
                  key={b.v}
                  size="small"
                  variant={maxBand === b.v ? "contained" : "outlined"}
                  color={maxBand === b.v ? "error" : "secondary"}
                  onClick={() => setMaxBand(b.v)}
                  sx={{ textTransform: "none", minWidth: 0, px: 1.2, py: 0.25 }}
                >
                  {b.l}
                </MDButton>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {loading && (
        <Box sx={{ py: 8, textAlign: "center", color: C.muted }}>
          <CircularProgress size={22} /> <div style={{ marginTop: 8 }}>Loading themes…</div>
        </Box>
      )}

      {!loading && (!model || !allThemes.length) && (
        <Box sx={{ py: 6, textAlign: "center", color: C.muted }}>
          <MDTypography variant="h6" sx={{ fontWeight: 700, color: C.ink }}>
            No theme model yet.
          </MDTypography>
          <div style={{ marginTop: 6 }}>
            A Super Risk Admin can build it from the Dashboard tab.
          </div>
        </Box>
      )}

      {!loading && model && allThemes.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${C.line}`,
            borderRadius: "12px",
            overflow: "hidden",
            background: C.surface,
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={TH}>Theme</th>
                  <th style={TH}>Category</th>
                  <th style={TH}>Priority</th>
                  <th style={{ ...TH, textAlign: "right" }}>Recent</th>
                  <th style={{ ...TH, textAlign: "right" }}>Total</th>
                  <th style={TH} aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {rows.map((t, i) => {
                  const subs = (t.subThemes || [])
                    .filter((s) => !s.isOther && /[a-z]/i.test(s.name || ""))
                    .map((s) => clean(s.name));
                  return (
                    <tr
                      key={i}
                      role="button"
                      tabIndex={0}
                      onClick={() => openTheme(t.theme)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") openTheme(t.theme);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={TD}>
                        <div style={{ fontWeight: 700, color: C.ink }}>{clean(t.theme)}</div>
                        {subs.length > 0 && (
                          <MDTypography variant="caption" sx={{ color: C.muted, marginTop: "3px" }}>
                            {subs.join(" · ")}
                          </MDTypography>
                        )}
                        {t.riskCount > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 700,
                                color: "#52009b",
                                background: "#ede7f6",
                                border: "1px solid #ce93d8",
                                borderRadius: 6,
                                padding: "1px 6px",
                              }}
                            >
                              ⚑ {t.riskCount} watchlist risk{t.riskCount > 1 ? "s" : ""}
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={{ ...TD, color: C.muted, fontSize: "0.78rem" }}>{clean(t.l1)}</td>
                      <td style={TD}>
                        <PriorityBadge p={t.prioritisation} />
                        {t.noNews && (
                          <MDTypography variant="caption" sx={{ color: C.muted, marginTop: "3px" }}>
                            on radar
                          </MDTypography>
                        )}
                      </td>
                      <td
                        style={{
                          ...TD,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          color: C.hot,
                          fontWeight: 650,
                        }}
                      >
                        {nf(t.recentAlerts)}
                      </td>
                      <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {nf(t.alertCount)}
                      </td>
                      <td style={{ ...TD, textAlign: "right" }}>
                        <span style={{ color: C.accent, fontWeight: 700 }}>›</span>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td style={{ ...TD, textAlign: "center", color: C.muted }} colSpan={7}>
                      No themes match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </MDBox>
  );
}

export default AnticipateEmergingThemesV2;
